/* The top of the page: two speedometers (PoD received %, PoD on time %) against the targets, and the few numbers behind
   them — delivered, pending (and how old), and how PoDs came in (API vs manual). Every number opens its shipments. */
import React from 'react'
import { Box, ButtonBase, Card, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PodGauge from './PodGauge'
import { TONE, fmtDate, fmtHours, num } from './podUtils'
import { CountUp, enter, fadeUp, growX } from './podMotion'

// Key-number tiles: white cards on a tinted panel; a coloured edge marks the ones that need action.
const tileSx = (accent) => ({
  display: 'block', width: '100%', textAlign: 'left', borderRadius: 2, px: 1.5, py: 1.25, minHeight: 44, bgcolor: 'background.paper',
  border: 1, borderColor: 'divider', boxShadow: accent ? `inset 3px 0 0 ${accent}, 0 1px 2px rgba(16,24,40,.05)` : '0 1px 2px rgba(16,24,40,.05)',
  transition: 'border-color .15s, box-shadow .15s', '&:hover': { borderColor: 'primary.light' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
})
const selSx = { borderColor: 'primary.main', bgcolor: 'rgba(32,101,209,.04)' }
const labelSx = { fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary' }

function Stat({ label, value, sub, accent, onClick, selected }) {
  return (
    <ButtonBase onClick={onClick} aria-pressed={!!selected} sx={{ ...tileSx(accent), ...(selected ? selSx : {}) }}>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.25, mt: 0.25, color: accent || 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{sub}</Typography>}
    </ButtonBase>
  )
}

/** API vs manual as one split bar with both parts labelled in text; each half lists its PoDs. */
function SourceSplit({ api, manual, onPick, selected }) {
  const total = (api?.received || 0) + (manual?.received || 0)
  const parts = [
    { key: 'API', name: 'API', s: api, color: 'primary.main' },
    { key: 'MANUAL', name: 'Manual', s: manual, color: 'grey.500' },
  ]
  return (
    <Box sx={{ ...tileSx(), '&:hover': {} }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography sx={labelSx}>How PoDs came in</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{num(total)} received</Typography>
      </Box>
      <Box key={total} sx={{ display: 'flex', gap: '2px', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: 'grey.200', mt: 1, transformOrigin: 'left center',
        ...enter(growX, 900, 200) }} aria-hidden>
        {total > 0 && parts.map((p) => (p.s?.received ? <Box key={p.key} sx={{ flex: p.s.received, bgcolor: p.color }} /> : null))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
        {parts.map((p) => {
          const n = p.s?.received || 0
          return (
            <ButtonBase key={p.key} onClick={() => onPick(p.key)} disabled={!n} aria-pressed={selected === p.key}
              sx={{ display: 'block', textAlign: 'left', borderRadius: 1.5, px: 1, py: 0.75, border: 1, borderColor: selected === p.key ? 'primary.main' : 'transparent',
                bgcolor: selected === p.key ? 'rgba(32,101,209,.04)' : TONE.neutral.bg, '&:hover': { borderColor: 'primary.light' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} aria-hidden />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{p.name}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', ml: 'auto' }}>{total ? `${Math.round((100 * n) / total)}%` : ''}</Typography>
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}><CountUp value={n} /></Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{p.s?.avgUploadHours != null ? `avg ${fmtHours(p.s.avgUploadHours)} to PoD` : 'no upload times yet'}</Typography>
            </ButtonBase>
          )
        })}
      </Box>
    </Box>
  )
}

export default function PodHeadline({ summary, loading, rows, onShow }) {
  const t = summary?.totals
  const labels = summary?.bucketLabels || []
  const last = labels.length - 1
  const old = last >= 0 ? t?.pendingByBucket?.[last] || 0 : 0
  const src = (k) => (summary?.bySource || []).find((s) => s.key === k)
  const tracked = (t?.onTime || 0) + (t?.late || 0)
  // numbers count up from 0 on load and from the last value when a filter changes
  const v = (x) => (loading && !summary ? '…' : x == null ? '—' : <CountUp value={x} />)
  const is = (st, extra = {}) => rows.podStatus === st && Object.entries({ agingBucket: null, timeliness: '', podSource: '', ...extra }).every(([k, x]) => rows[k] === x)

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2, p: { xs: 1.5, md: 2.5 }, ...enter(fadeUp, 500) }}>
      <Box sx={{ display: 'grid', gap: { xs: 3, md: 2 }, alignItems: 'start',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'minmax(0,1fr) minmax(0,1fr) minmax(280px,.9fr)' } }}>
        <PodGauge title='PoD received' value={t?.receivedPct} target={summary?.receivedTargetPct ?? 95} loading={loading}
          sub={t ? `${num(t.received)} of ${num(t.delivered)} delivered shipments have a PoD` : ''}
          action={t?.pending ? `See ${num(t.pending)} pending` : null} onAction={() => onShow({ podStatus: 'PENDING' })} />
        <PodGauge title={`PoD on time (within ${summary?.onTimeHours ?? 24} h)`} value={t?.onTimePct} target={summary?.onTimeTargetPct ?? 90} loading={loading}
          sub={t ? (tracked ? `${num(t.onTime)} on time · ${num(t.late)} late` : 'No PoD with an upload time yet') : ''}
          action={t?.late ? `See ${num(t.late)} late` : null} onAction={() => onShow({ podStatus: 'RECEIVED', timeliness: 'LATE' })} />

        <Box sx={{ gridColumn: { sm: '1 / -1', lg: 'auto' }, bgcolor: TONE.neutral.bg, borderRadius: 3, p: 1.25,
          display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'start', gap: 1 }}>
          <Stat label='Delivered' value={v(t?.delivered)} sub='PTL shipments in these dates' onClick={() => onShow({})} />
          <Stat label='PoD pending' value={v(t?.pending)} accent={t?.pending ? TONE.error.fg : undefined}
            sub={t ? (t.pending ? `oldest ${t.oldestPendingDays ?? 0} days` : 'nothing pending') : ''}
            onClick={() => onShow({ podStatus: 'PENDING' })} selected={is('PENDING')} />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Stat label={`Pending ${labels[last]?.toLowerCase() || 'longest'}`} value={v(t ? old : null)} accent={old ? TONE.error.fg : undefined}
              sub={old ? 'The oldest group: chase these first' : 'None in the oldest group'}
              onClick={() => onShow({ podStatus: 'PENDING', agingBucket: last })} selected={rows.podStatus === 'PENDING' && rows.agingBucket === last} />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <SourceSplit api={src('API')} manual={src('MANUAL')} selected={rows.podSource} onPick={(k) => onShow({ podStatus: 'RECEIVED', podSource: k })} />
          </Box>
        </Box>
      </Box>
      {t?.notTracked > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 2, color: 'text.secondary' }}>
          <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
          <Typography sx={{ fontSize: 12 }}>
            {num(t.notTracked)} PoDs came in before upload times were recorded{summary?.trackingStartDate ? ` (${fmtDate(summary.trackingStartDate)})` : ''}, so
            they are left out of the on-time %.{' '}
            <Tooltip title="Whether they came by API or manually is taken from each courier's PoD via setting on the PTL courier master.">
              <Box component='span' tabIndex={0} sx={{ textDecoration: 'underline dotted', cursor: 'help' }}>How is API / manual decided?</Box>
            </Tooltip>
          </Typography>
        </Box>
      )}
    </Card>
  )
}
